import { ToastContext, ToastTypeEnum } from "context/toastContext";
import {
  CreateOrUpdateCompanyCandidateLoginMutation,
  CreateOrUpdateCompanyCandidateMutation,
  useCreateOrUpdateCompanyCandidateLoginMutation,
  useCreateOrUpdateCompanyCandidateMutation,
} from "@generated/graphql";
import { useContext } from "react";
import { CandidateType } from "constants/global";
import { FetchResult } from "@apollo/client";
import { parseApolloError } from "helpers/errorsParser";
import { CandidateInputs } from "../types";

const isCandidateWithTelegramData = (value: unknown): value is CreateOrUpdateCompanyCandidateMutation => {
  return value !== null && typeof value === "object" && "createOrUpdateCompanyCandidate" in value;
};

type UseUpdateCandidateParams = {
  setIsEditing: (value: boolean) => void;
  onSuccess?: ((id: string) => void) | null;
};

const useUpdateCandidate = ({ setIsEditing, onSuccess }: UseUpdateCandidateParams) => {
  const { addToast } = useContext(ToastContext);

  const toggleToastOnSuccess = (text?: string) => addToast({ type: ToastTypeEnum.SUCCESS, text });

  const [updateCompanyCandidate, { loading }] = useCreateOrUpdateCompanyCandidateMutation();

  const [updateCompanyCandidateLogin, { loading: updateCompanyCandidateLoginLoading }] =
    useCreateOrUpdateCompanyCandidateLoginMutation();

  const updateCompanyCandidateLoading = loading || updateCompanyCandidateLoginLoading;

  const onSuccessUpdate = (
    data: CreateOrUpdateCompanyCandidateMutation | CreateOrUpdateCompanyCandidateLoginMutation | null | undefined
  ) => {
    toggleToastOnSuccess();
    setIsEditing(false);

    if (onSuccess) {
      let candidateId: string | null = null;

      if (isCandidateWithTelegramData(data)) {
        candidateId = data.createOrUpdateCompanyCandidate.id;
      } else if (data) {
        candidateId = data.createOrUpdateCompanyCandidateLogin.id;
      }

      if (candidateId) onSuccess(candidateId);
    }
  };

  const onUpdateCompanyCandidate = (formData: CandidateInputs) => {
    const isTelegramCandidate = formData.candidateType === CandidateType.Telegram;
    const basicData = {
      id: formData.id,
      email: formData.email,
      fullName: formData.fullName || "",
      organization: formData.organization,
      position: formData.position,
      grade: formData.grade?.id,
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      type: formData.type?.id!,
      link: formData.link,
      cvFile: formData.cvFile instanceof File || formData.cvFile === null ? formData.cvFile : undefined,
    };

    let updatePromise: Promise<
      FetchResult<CreateOrUpdateCompanyCandidateMutation | CreateOrUpdateCompanyCandidateLoginMutation>
    >;

    if (isTelegramCandidate) {
      updatePromise = updateCompanyCandidate({
        variables: {
          input: {
            ...basicData,
            telegramUsername: formData.telegramUsername.replaceAll("@", "") || "",
          },
        },
      });
    } else {
      updatePromise = updateCompanyCandidateLogin({
        variables: {
          input: {
            ...basicData,
            login: formData.login || "",
            password: formData.password || "",
          },
        },
      });
    }

    updatePromise
      .then(({ data }) => onSuccessUpdate(data))
      .catch((e) => parseApolloError(e, { addToast, defaultText: "Ошибка сохранения кандидата" }));
  };

  return { onUpdateCompanyCandidate, updateCompanyCandidateLoading };
};

export default useUpdateCandidate;
